
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Download, Check, ArrowUpRight, ShoppingCart, ChevronLeft, ChevronRight
} from '../components/Icons';
import { Card, Table, Thead, Tbody, Tr, Th, Td, PageHeader, Button } from '../components/UI';
import { inventoryData } from '../data/inventory';
import { useTranslation } from '../contexts/LanguageContext';
import { StockStatus, InventoryItem } from '../types';
import { useCart } from '../contexts/CartContext';

const StockList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [globalSearch, setGlobalSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [viewingProduct, setViewingProduct] = useState<InventoryItem | null>(null);

  // Derive unique suppliers for filter (kept for reference or if we want a dropdown in column)
  const suppliers = useMemo(() => Array.from(new Set(inventoryData.map(item => item.supplier))), []);

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  // Filter Logic
  const filteredData = useMemo(() => {
    return inventoryData.filter(item => {
      // 1. Global Search
      const searchLower = globalSearch.toLowerCase();
      const matchesGlobal = 
        item.itemNumber.toLowerCase().includes(searchLower) ||
        item.itemName.toLowerCase().includes(searchLower) ||
        item.supplier.toLowerCase().includes(searchLower);
      
      // 2. Status Tab
      const matchesStatus = statusFilter === 'All' || item.status === statusFilter;

      // 3. Column Filters
      const matchesColumns = Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((item as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });

      return matchesGlobal && matchesStatus && matchesColumns;
    });
  }, [globalSearch, statusFilter, columnFilters]);

  const handleAddToCart = (item: InventoryItem) => {
    const defaultQty = item.minStock > 0 ? Math.ceil(item.minStock * 0.1) : 10; 
    const mockPrice = 25.00;
    addItem(item, defaultQty, mockPrice);
  };

  const getStatusBadge = (status: StockStatus) => {
    switch (status) {
      case StockStatus.HEALTHY:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30">
            <Check size={10} strokeWidth={4} /> {t('healthy')}
          </span>
        );
      case StockStatus.CRITICAL:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30">
            {t('critical')}
          </span>
        );
      case StockStatus.WARNING:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/30">
            {t('warning')}
          </span>
        );
      default:
        return null;
    }
  };

  const tabs = ['All', ...Object.values(StockStatus)];

  if (viewingProduct) {
    // ... (Keep existing detailed view logic)
    return (
        <div className="p-6 lg:p-10 space-y-10 max-w-[1920px] mx-auto animate-in slide-in-from-right duration-500">
            <PageHeader title="VMI Detail View" breadcrumbs={[{ label: 'Stock List' }, { label: viewingProduct.itemNumber }]} onBack={() => setViewingProduct(null)} actions={<div className="flex gap-3"><Button size="sm" variant="outline" onClick={() => handleAddToCart(viewingProduct)}><ShoppingCart size={14} className="mr-2" /> Add to Request</Button><Button size="sm" onClick={() => navigate('/replenishment')}>Manage Consignment</Button></div>} />
            <section><Card className="p-0 overflow-hidden bg-white dark:bg-[#111111] border-gray-200 dark:border-[#262626]"><div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 divide-x divide-y divide-gray-100 dark:divide-[#262626] border-b border-gray-100 dark:border-[#262626]"><div className="p-6 space-y-1"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Item Number</p><p className="text-sm font-bold text-brand-dark dark:text-white font-mono">{viewingProduct.itemNumber}</p></div><div className="p-6 space-y-1"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Supplier</p><p className="text-sm font-bold text-brand-dark dark:text-white truncate">{viewingProduct.supplier}</p></div><div className="p-6 space-y-1"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Stock Level</p><p className={`text-2xl font-black ${viewingProduct.currentStock < viewingProduct.minStock ? 'text-red-600' : 'text-brand-dark dark:text-white'}`}>{viewingProduct.currentStock}</p></div><div className="p-6 space-y-1"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Min. Threshold</p><p className="text-sm font-bold text-brand-dark dark:text-white">{viewingProduct.minStock}</p></div><div className="p-6 space-y-1"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Max. Capacity</p><p className="text-sm font-bold text-brand-dark dark:text-white">500</p></div><div className="p-6 space-y-1"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Status</p><div className="flex">{getStatusBadge(viewingProduct.status)}</div></div></div></Card></section>
        </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('stock_list')} 
        breadcrumbs={[{ label: 'VMI Hub' }, { label: t('stock_list') }]}
      />

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#393939] bg-white dark:bg-[#161616] flex flex-wrap items-center justify-between gap-4">
          
          {/* Tab Switch */}
          <div className="flex items-center gap-1 bg-[#F5F7FA] dark:bg-[#262626] p-1 rounded-md">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`h-8 px-4 text-[10px] font-bold uppercase tracking-widest transition-all rounded-sm ${
                  statusFilter === tab
                    ? 'bg-white dark:bg-[#161616] text-brand-dark dark:text-white shadow-sm'
                    : 'text-gray-500 hover:text-brand-dark dark:hover:text-white'
                }`}
              >
                {tab === 'All' ? t('all') : tab}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
             <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-medium transition-colors" />
                <input 
                  type="text" 
                  placeholder={t('search_resources')} 
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  className="h-9 pl-9 pr-4 bg-[#F5F7FA] dark:bg-[#262626] border border-transparent focus:border-brand-medium focus:bg-white dark:focus:bg-[#1F1F1F] rounded-sm text-xs w-64 outline-none transition-all"
                />
             </div>

             <div className="h-4 w-px bg-gray-300 dark:bg-gray-700 mx-1"></div>

             <button 
               className="h-9 px-4 flex items-center gap-2 bg-white dark:bg-[#262626] border border-gray-200 dark:border-[#393939] hover:bg-gray-50 dark:hover:bg-[#333] text-gray-600 dark:text-gray-300 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-all"
             >
                <Download size={14} /> {t('export_list')}
             </button>
          </div>
        </div>

        <Table>
          <Thead>
            <Tr isHeader>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_number')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('itemNumber', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_name')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('supplier')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('supplier', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('current_stock')}</span>
                  <input 
                    className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('currentStock', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('min_stock')}</span>
                  <input 
                    className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('minStock', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('status')}</span>
                  <select 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                    onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                  >
                    <option value="">{t('all')}</option>
                    <option value="Healthy">{t('healthy')}</option>
                    <option value="Critical">{t('critical')}</option>
                    <option value="Warning">{t('warning')}</option>
                  </select>
                </div>
              </Th>
              <Th className="text-right">
                 {t('actions')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.length > 0 ? filteredData.map((item) => (
              <Tr key={item.id} onClick={() => setViewingProduct(item)}>
                <Td>
                  <span className="font-bold text-brand-dark dark:text-white font-mono text-xs">{item.itemNumber}</span>
                </Td>
                <Td className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {item.itemName}
                </Td>
                <Td className="text-xs text-gray-500 dark:text-gray-400">
                  {item.supplier}
                </Td>
                <Td className="text-right">
                  <span className={`text-sm font-bold ${item.currentStock < item.minStock ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>
                    {item.currentStock.toLocaleString()}
                  </span>
                </Td>
                <Td className="text-right text-xs text-gray-500 font-medium">
                  {item.minStock.toLocaleString()}
                </Td>
                <Td>
                  {getStatusBadge(item.status)}
                </Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}
                      className="p-2 rounded-sm text-gray-400 hover:text-brand-dark dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#262626] transition-colors"
                      title={t('add_to_cart')}
                    >
                      <ShoppingCart size={14} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setViewingProduct(item); }}
                      className={`p-2 rounded-sm transition-colors ${
                        item.status === StockStatus.CRITICAL 
                          ? 'bg-brand-medium text-white hover:bg-brand-dark shadow-sm' 
                          : 'text-gray-400 hover:text-brand-dark dark:hover:text-white'
                      }`}
                    >
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </Td>
              </Tr>
            )) : (
              <Tr>
                <Td colSpan={7} className="text-center py-12 text-xs text-gray-400 uppercase tracking-widest font-bold">
                  No items found matching your filters.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-[#393939] flex items-center justify-between gap-2 bg-gray-50/50 dark:bg-[#161616]">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {t('showing_x_of_y').replace('{x}', String(filteredData.length)).replace('{y}', String(filteredData.length))}
          </div>
          <div className="flex items-center gap-1">
            <button className="p-1.5 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"><ChevronLeft size={16} /></button>
            <button className="p-1.5 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StockList;
